import { Grid2, Skeleton, Typography, Box, Card, CardContent, Alert } from "@mui/material";
import SubscriptionCard from "@shop/components/SubscriptionCard";
import React from "react";
import { updateSelectedProduct, useShopSlice } from "@store/slices/shop";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import { FETCH_COMPANY } from "@company/graphql/queries/fetchCompany";
import { State } from "@store/state";

const features = {
    S: ["250 zusätzliche Seiten", "Sofort verfügbar", "Einmalige Zahlung"],
    M: ["2.500 zusätzliche Seiten", "Sofort verfügbar", "Einmalige Zahlung", "Besserer Preis pro Seite"],
    L: ["5.000 zusätzliche Seiten", "Sofort verfügbar", "Einmalige Zahlung", "Bester Preis pro Seite", "Ideal für große Projekte"],
};

const ExtraPagesSelection = () => {
    const dispatch = useDispatch();

    const { products, selectedProducts } = useShopSlice();
    const { data: companyData, loading: companyLoading } = useQuery(FETCH_COMPANY);

    console.log("🎯 ExtraPagesSelection - Products:", products);

    const extraPagesStripe = products.find((product: State.Product) => product.name.includes("Seitenkontingent"))?.prices || [];

    console.log("🎯 ExtraPagesSelection - Extra pages Stripe:", extraPagesStripe);

    // 🔧 Filter to only one-time prices (exclude recurring legacy prices)
    const oneTimePrices = extraPagesStripe.filter((price: State.Price) => !price.interval);

    // Sort prices by amount
    const sortedPrices = [...oneTimePrices].sort((a, b) => a.amount - b.amount);

    // Get prices for S, M, L packages (assuming they are in order)
    const sPriceData = sortedPrices[0];
    const mPriceData = sortedPrices[1];
    const lPriceData = sortedPrices[2];

    // Get current quantity for a price ID from selected products
    const getCurrentQuantity = (priceId: string) => {
        const selected = selectedProducts.find((p: any) => p.priceId === priceId);
        return selected?.quantity || 0;
    };

    // Calculate quota statistics
    const company = companyData?.company;
    const totalUserQuota = company?.users?.reduce((sum: number, user: any) => sum + user.pageQuota, 0) || 0;
    const totalUsedPages = company?.users?.reduce((sum: number, user: any) => sum + user.usedPages, 0) || 0;
    const freePageQuota = company?.freePageQuota || 0;
    const totalAvailablePages = totalUserQuota - totalUsedPages + freePageQuota;

    if (products.length === 0) {
        return (
            <Grid2 container size={12} spacing={4.5} justifyContent={"center"}>
                <Grid2 size={12} container spacing={3} justifyContent={"center"}>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                    <Grid2>
                        <Skeleton variant="rectangular" width={260} height={360} sx={{ borderRadius: 2 }} />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    }
    
    const handleQuantityChange = (quantity: number, priceId: string) => {
        if (!priceId) return;

        dispatch(updateSelectedProduct({ priceId, quantity }));
    };

    return (
        <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    Zusätzliche Seiten kaufen
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Erweitern Sie Ihr Seitenkontingent mit einmaligen Paketen. 
                    Die Seiten werden sofort zu Ihrem verfügbaren Kontingent hinzugefügt.
                </Typography>
            </Box>

            {/* Current Quota Status */}
            {false && !companyLoading && company && (
                <Card sx={{ mb: 4, bgcolor: 'background.paper' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Ihr aktuelles Seitenkontingent
                        </Typography>
                        <Grid2 container spacing={3}>
                            <Grid2 size={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                                        {totalAvailablePages.toLocaleString('de-DE')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Verfügbare Seiten
                                    </Typography>
                                </Box>
                            </Grid2>
                            <Grid2 size={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="text.secondary" fontWeight="bold">
                                        {totalUsedPages.toLocaleString('de-DE')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Bereits verwendet
                                    </Typography>
                                </Box>
                            </Grid2>
                            <Grid2 size={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main" fontWeight="bold">
                                        {freePageQuota.toLocaleString('de-DE')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Firmenkontingent
                                    </Typography>
                                </Box>
                            </Grid2>
                        </Grid2>
                        
                        {totalAvailablePages < 1000 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Ihr Seitenkontingent wird knapp. Erwägen Sie den Kauf zusätzlicher Seiten, 
                                um sicherzustellen, dass Sie alle Ihre Dokumente befragen können.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}
            
            <Grid2 container size={12} spacing={3} justifyContent={"center"}>
                <Grid2 size={12} container spacing={3} justifyContent={"center"}>
                    <Grid2>
                        <SubscriptionCard
                            title={"250 Seiten"}
                            monthlyPrice={sPriceData?.amount / 100 || 0}
                            priceInterval={"once"}
                            productType="ONE_TIME_PURCHASE"
                            features={features["S"]}
                            priceId={sPriceData?.priceId}
                            onQuantityChange={(quantity) => handleQuantityChange(quantity, sPriceData?.priceId || "")}
                            initialQuantity={getCurrentQuantity(sPriceData?.priceId || "")}
                        />
                    </Grid2>
                    <Grid2>
                        <SubscriptionCard
                            title={"2.500 Seiten"}
                            monthlyPrice={mPriceData?.amount / 100 || 0}
                            priceInterval={"once"}
                            productType="ONE_TIME_PURCHASE"
                            features={features["M"]}
                            priceId={mPriceData?.priceId}
                            onQuantityChange={(quantity) => handleQuantityChange(quantity, mPriceData?.priceId || "")}
                            initialQuantity={getCurrentQuantity(mPriceData?.priceId || "")}
                            isHighlight={true}
                        />
                    </Grid2>

                    <Grid2>
                        <SubscriptionCard
                            title={"5.000 Seiten"}
                            monthlyPrice={lPriceData?.amount / 100 || 0}
                            priceInterval={"once"}
                            productType="ONE_TIME_PURCHASE"
                            features={features["L"]}
                            priceId={lPriceData?.priceId}
                            onQuantityChange={(quantity) => handleQuantityChange(quantity, lPriceData?.priceId || "")}
                            initialQuantity={getCurrentQuantity(lPriceData?.priceId || "")}
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default ExtraPagesSelection;
